import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import {
  DeleteGroup,
  GetUserGroupById,
  UpdateUserGroup,
} from "../../../hooks/useUserGroupdata";
import { GetFacilitiesByOrganisationId } from "../../../hooks/useFacilityData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const AVATAR_GRADIENTS = [
  ["#8a1125", "#c23b54"],
  ["#10324a", "#2d5a7e"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ name, size = 64 }) => {
  const [a, b] = gradientFor(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "16px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: `0 1px 0 rgba(255,255,255,0.4) inset, 0 14px 30px -14px ${a}66`,
        transition: "background 0.4s ease",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.24), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: "-0.02em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initialsFor(name)}
      </Typography>
    </Box>
  );
};

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

const FacilitySelect = ({ value, onChange, facilities, loading }) => (
  <FormControl fullWidth>
    <Select
      value={value}
      onChange={onChange}
      displayEmpty
      disabled={loading}
      IconComponent={KeyboardArrowDownRoundedIcon}
      MenuProps={{
        PaperProps: {
          sx: {
            mt: 1,
            borderRadius: "12px",
            border: `1px solid ${LINE}`,
            boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
            "& .MuiMenuItem-root": {
              fontSize: "14px",
              fontFamily: '"DM Sans", sans-serif',
              py: 1.25,
              px: 2,
              "&.Mui-selected": {
                background: "rgba(10,22,40,0.04)",
                "&:hover": { background: "rgba(10,22,40,0.06)" },
              },
            },
          },
        },
      }}
      sx={{
        height: 48,
        background: "#fff",
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& .MuiSelect-select": {
          py: 0,
          display: "flex",
          alignItems: "center",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: LINE,
          borderRadius: "12px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(10,22,40,0.2)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(10,22,40,0.32)",
          borderWidth: "1px",
        },
        "& .MuiSelect-icon": { color: INK_MUTED },
      }}
    >
      <MenuItem value="">
        <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
          Ingen anläggning
        </Box>
      </MenuItem>
      {facilities.map((facility) => (
        <MenuItem key={facility.id} value={facility.id}>
          {facility.facility_name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const PermissionToggle = ({ icon: Icon, title, description, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2.25,
      p: 2.25,
      width: "100%",
      textAlign: "left",
      cursor: "pointer",
      background: active ? "rgba(138,17,37,0.025)" : "#fff",
      border: `1px solid ${active ? "rgba(138,17,37,0.28)" : LINE}`,
      borderRadius: "14px",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
      fontFamily: '"DM Sans", sans-serif',
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: BRAND,
        transform: active ? "scaleY(1)" : "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: active ? "rgba(138,17,37,0.4)" : "rgba(10,22,40,0.2)",
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        minWidth: 44,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: active
          ? `linear-gradient(135deg, ${BRAND}, #c23b54)`
          : "#f4f2ee",
        color: active ? "#fff" : INK_MUTED,
        transition: "all 0.35s ease",
        boxShadow: active
          ? "0 10px 22px -10px rgba(138,17,37,0.5)"
          : "none",
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: "16px",
          color: INK,
          letterSpacing: "-0.005em",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "12.5px",
          color: INK_MUTED,
          mt: 0.35,
          fontFamily: '"DM Sans", sans-serif',
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    </Box>
    <Box
      sx={{
        width: 42,
        height: 24,
        borderRadius: "999px",
        background: active ? BRAND : "rgba(10,22,40,0.14)",
        position: "relative",
        transition: "background 0.3s ease",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 2,
          left: 2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 2px 6px rgba(10,22,40,0.3)",
          transform: active ? "translateX(18px)" : "translateX(0)",
          transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

export const GroupEditModal = ({ groupId, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [edited, setEdited] = useState(false);
  const [groupData, setGroupData] = useState({});
  const { data: group, isLoading } = GetUserGroupById(groupId);
  const [canAlarm, setCanAlarm] = useState(false);
  const [canChat, setCanChat] = useState(false);
  const [facilityId, setFacilityId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const organisationId = groupData?.organisation_id;
  const { data: facilitiesData, isLoading: facilitiesLoading } =
    GetFacilitiesByOrganisationId(organisationId);
  const facilities = facilitiesData?.data || [];

  useEffect(() => {
    if (group?.data) {
      setGroupData(group.data);
      setCanAlarm(group.data.can_alarm);
      setCanChat(group.data.can_chat);
      setFacilityId(group.data.facility_id || "");
    }
  }, [group]);

  useEffect(() => {
    if (!confirmDelete) return undefined;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const deleteGroupMutation = DeleteGroup({
    onSuccess: () => {
      enqueueSnackbar("Grupp raderad.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte radera grupp.", { variant: "error" });
    },
  });

  const updateGroupMutation = UpdateUserGroup({
    onSuccess: () => {
      enqueueSnackbar("Grupp uppdaterad.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte uppdatera grupp.", { variant: "error" });
    },
  });

  const handleDeleteClick = () => {
    if (confirmDelete) {
      deleteGroupMutation.mutate(groupId);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleUpdate = () => {
    const payload = {
      groupId,
      groupData: {
        ...groupData,
        can_alarm: canAlarm,
        can_chat: canChat,
        facility_id: facilityId ? parseInt(facilityId) : null,
      },
      organisationId: groupData?.organisation_id,
    };
    updateGroupMutation.mutate(payload);
    setEdited(false);
  };

  const name = groupData.group_name || "";
  const saving = updateGroupMutation.isLoading;
  const deleting = deleteGroupMutation.isLoading;
  const canSave = edited && !saving && name.trim().length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          mb: 4,
          pb: 3,
          borderBottom: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Avatar name={name || "?"} size={64} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
              mb: 0.5,
            }}
          >
            Grupp
          </Typography>
          {isLoading && !name ? (
            <Skeleton width="65%" height={30} />
          ) : (
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "1.625rem",
                fontWeight: 600,
                color: INK,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name || "Namnlös grupp"}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Identitet</SectionLabel>
        <Stack spacing={2.25}>
          <Box>
            <FieldLabel>Gruppnamn</FieldLabel>
            <TextInput
              value={groupData.group_name || ""}
              placeholder="T.ex. Administratörer"
              onChange={(e) => {
                setGroupData({ ...groupData, group_name: e.target.value });
                setEdited(true);
              }}
            />
          </Box>
          <Box>
            <FieldLabel>Tillhör anläggning</FieldLabel>
            <FacilitySelect
              value={facilityId}
              onChange={(e) => {
                setFacilityId(e.target.value);
                setEdited(true);
              }}
              facilities={facilities}
              loading={facilitiesLoading}
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="02">Behörigheter</SectionLabel>
        <Stack spacing={1.5}>
          <PermissionToggle
            icon={NotificationsActiveRoundedIcon}
            title="Kan larma"
            description="Tillåt gruppens medlemmar att trigga och hantera larm"
            active={canAlarm}
            onClick={() => {
              setCanAlarm((v) => !v);
              setEdited(true);
            }}
          />
          <PermissionToggle
            icon={ChatBubbleRoundedIcon}
            title="Kan chatta"
            description="Tillåt gruppens medlemmar att använda chatfunktioner"
            active={canChat}
            onClick={() => {
              setCanChat((v) => !v);
              setEdited(true);
            }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleDeleteClick}
          disabled={deleting}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            background: confirmDelete ? BRAND : "transparent",
            color: confirmDelete ? "#fff" : BRAND,
            border: `1px solid ${
              confirmDelete ? BRAND : "rgba(138,17,37,0.26)"
            }`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: deleting ? "wait" : "pointer",
            transition: "all 0.25s ease",
            opacity: deleting ? 0.6 : 1,
            "&:hover": {
              background: confirmDelete ? "#6b0d1d" : "rgba(138,17,37,0.06)",
              borderColor: BRAND,
            },
          }}
        >
          {deleting ? (
            <CircularProgress size={14} sx={{ color: BRAND }} />
          ) : (
            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
          )}
          {confirmDelete ? "Klicka igen för att bekräfta" : "Ta bort grupp"}
        </Box>

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              px: 2.25,
              py: 1.25,
              background: "transparent",
              color: INK_MUTED,
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.25s ease",
              "&:hover": {
                color: INK,
                borderColor: "rgba(10,22,40,0.22)",
              },
            }}
          >
            Avbryt
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleUpdate}
            disabled={!canSave}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.75,
              py: 1.25,
              background: INK,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: canSave ? "pointer" : "not-allowed",
              opacity: canSave ? 1 : 0.4,
              transition: "all 0.25s ease",
              boxShadow: canSave
                ? "0 10px 24px -12px rgba(10,22,40,0.55)"
                : "none",
              "&:hover": canSave
                ? {
                    background: "#1a2638",
                    transform: "translateY(-1px)",
                  }
                : {},
            }}
          >
            {saving ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <CheckRoundedIcon sx={{ fontSize: 16 }} />
            )}
            Spara ändringar
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
